import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {Heart, Users, BookOpen, Calendar, Music, Baby, Sparkles, Quote, ArrowRight} from 'lucide-react';
import { motion } from 'framer-motion';
import { lumi } from '../lib/lumi';
import PrayerWall from '../components/PrayerWall';

const Home: React.FC = () => {
  const [recentPrayers, setRecentPrayers] = useState<any[]>([]);
  const [loadingPrayers, setLoadingPrayers] = useState(true);
  const [recentTestimonials, setRecentTestimonials] = useState<any[]>([]);
  const [loadingTestimonials, setLoadingTestimonials] = useState(true);

  useEffect(() => {
    fetchRecentPrayers();
    fetchRecentTestimonials();
  }, []);

  const fetchRecentPrayers = async () => {
    try {
      const { list } = await lumi.entities.prayers.list({
        filter: {
          isPrivate: false,
          status: 'APPROVED'
        },
        sort: { createdAt: -1 },
        limit: 5
      });
      setRecentPrayers(list);
    } catch (error) {
      console.error('Erro ao carregar pedidos de oração:', error);
    } finally {
      setLoadingPrayers(false);
    }
  };

  const fetchRecentTestimonials = async () => {
    try {
      const { list } = await lumi.entities.testimonials.list({
        filter: { status: { $in: ['APPROVED', 'aprovado'] } },
        sort: { createdAt: -1 },
        limit: 12
      });
      // Deduplicar por _id antes de exibir
      const seen = new Set<string>();
      const unique = list.filter((t: any) => {
        if (!t._id || seen.has(t._id)) return false;
        seen.add(t._id);
        return true;
      });
      setRecentTestimonials(unique.slice(0, 6));
    } catch (error) {
      console.error('Erro ao carregar testemunhos:', error);
    } finally {
      setLoadingTestimonials(false);
    }
  };

  const features = [
  {
    icon: Heart,
    title: 'Caixa de Oração',
    description: 'Compartilhe seus pedidos de oração conosco',
    link: '/oracao',
    gradient: 'from-amber-400 to-orange-500'
  },
  {
    icon: Users,
    title: 'Testemunhos',
    description: 'Testemunhos de vidas transformadas',
    link: '/testemunhos',
    gradient: 'from-blue-500 to-indigo-600'
  },
  {
    icon: BookOpen,
    title: 'Palavra de Deus',
    description: 'Mensagens que edificam e fortalecem a fé',
    link: '/palavra-de-deus',
    gradient: 'from-purple-500 to-pink-500'
  },
  {
    icon: Calendar,
    title: 'Eventos',
    description: 'Participe dos nossos encontros e celebrações',
    link: '/eventos',
    gradient: 'from-green-500 to-teal-500'
  },
  {
    icon: Music,
    title: 'Louvores',
    description: 'Biblioteca de músicas que adoram ao Senhor',
    link: '/louvores',
    gradient: 'from-cyan-500 to-blue-500'
  },
  {
    icon: Baby,
    title: 'Programação',
    description: 'Lives, cultos e atividades do ministério',
    link: '/programacao',
    gradient: 'from-pink-400 to-rose-500'
  }];


  return (
    <div className="min-h-screen bg-white">
      {/* Floating Prayer Button */}
      <motion.div
        initial={{ scale: 0, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ delay: 1, duration: 0.5 }}
        className="fixed bottom-28 right-8 z-50">
        <Link
          to="/oracao"
          className="group flex items-center gap-3 bg-gradient-to-r from-orange-500 to-amber-500 text-white px-6 py-4 rounded-full shadow-2xl hover:shadow-orange-500/50 transition-all hover:scale-110 font-semibold">
          <Heart className="group-hover:animate-pulse" size={24} />
          <span className="hidden sm:inline">Pedir Oração</span>
        </Link>
      </motion.div>

      {/* Hero Section with Video Background */}
      <section className="relative h-[85vh] min-h-[600px] flex items-center justify-center overflow-hidden">
        {/* Video Background */}
        <div className="absolute inset-0">
          <video
            autoPlay
            loop
            muted
            playsInline
            className="absolute inset-0 w-full h-full object-cover"
            poster="https://images.unsplash.com/photo-1529070538774-1843cb3265df?q=80&w=2070">
            <source src="https://assets.mixkit.co/videos/preview/mixkit-group-of-people-partying-happily-4640-large.mp4" type="video/mp4" />
          </video>
          
          {/* Gradient Overlay */}
          <div className="absolute inset-0 bg-gradient-to-br from-[#0f172a]/90 via-[#1e293b]/85 to-[#334155]/80" />
        </div>

        {/* Decorative Light Elements */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute top-20 left-10 w-72 h-72 bg-amber-400/10 rounded-full blur-3xl animate-pulse" />
          <div className="absolute bottom-20 right-10 w-96 h-96 bg-blue-400/10 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }} />
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[500px] h-[500px] bg-white/5 rounded-full blur-3xl" />
        </div>

        {/* Content */}
        <div className="relative z-10 max-w-5xl mx-auto px-4 text-center">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, ease: "easeOut" }}>
            
            {/* Logo */}
            <motion.div
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ delay: 0.3, duration: 0.8 }}
              className="relative mx-auto mb-8 w-fit group">
              {/* Glow Effect */}
              <div className="absolute inset-0 bg-gradient-to-r from-amber-400/30 via-yellow-300/30 to-amber-500/30 rounded-3xl blur-2xl group-hover:blur-3xl transition-all duration-500 animate-pulse" />
              
              {/* Logo Container */}
              <div className="relative bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-xl rounded-3xl p-8 shadow-2xl border border-white/20 group-hover:border-amber-400/50 transition-all duration-500 group-hover:scale-105">
                <img
                  src="https://static.lumi.new/material/fd/fd208cdb7c6b89103dc6a3e704c4cafe.png"
                  alt="REFUGE IN CHRIST MINISTRY"
                  className="h-32 w-auto drop-shadow-2xl" />
              </div>
              
              {/* Shine Effect */}
              <div className="absolute inset-0 rounded-3xl bg-gradient-to-r from-transparent via-white/20 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-1000" />
            </motion.div>

            {/* Main Title - Serif Typography */}
            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5, duration: 0.8 }}
              className="text-5xl md:text-7xl font-serif font-bold text-white mb-6 leading-tight"
              style={{ fontFamily: 'Georgia, Cambria, "Times New Roman", serif' }}>
              Um Lugar de Refúgio e Fé
            </motion.h1>

            {/* Subtitle */}
            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.7, duration: 0.8 }}
              className="text-xl md:text-2xl text-white/95 mb-10 leading-relaxed max-w-3xl mx-auto font-light">
              Uma comunidade acolhedora onde você encontra paz, proteção espiritual e fortalecimento para sua jornada de fé.
            </motion.p>

            {/* CTA Buttons */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.9, duration: 0.8 }}
              className="flex flex-col sm:flex-row gap-5 justify-center">
              <Link
                to="/oracao"
                className="group relative overflow-hidden bg-gradient-to-r from-amber-400 to-yellow-500 text-[#0f172a] px-10 py-5 rounded-full font-bold text-lg hover:shadow-2xl hover:shadow-amber-500/50 transition-all hover:scale-105">
                <span className="relative z-10 flex items-center justify-center gap-2">
                  <Heart size={22} />
                  Pedir Oração Agora
                </span>
                <div className="absolute inset-0 bg-gradient-to-r from-yellow-300 to-amber-400 translate-y-full group-hover:translate-y-0 transition-transform duration-300" />
              </Link>
              
              <Link
                to="/sobre"
                className="group bg-white/10 backdrop-blur-md border-2 border-white/50 text-white px-10 py-5 rounded-full font-bold text-lg hover:bg-white hover:text-[#0f172a] transition-all hover:scale-105 hover:shadow-2xl">
                Conheça Nossa Missão
              </Link>
            </motion.div>

            {/* Decorative Sparkle */}
            <motion.div
              initial={{ opacity: 0, scale: 0 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 1.2, duration: 0.5 }}
              className="mt-12 flex justify-center">
              <Sparkles className="text-amber-400 animate-pulse" size={32} />
            </motion.div>
          </motion.div>
        </div>

        {/* Wave Divider */}
        <div className="absolute bottom-0 left-0 right-0">
          <svg viewBox="0 0 1440 120" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full h-auto">
            <path d="M0,64 C360,20 720,100 1080,64 C1440,28 1440,120 1440,120 L0,120 Z" fill="white" />
          </svg>
        </div>
      </section>

      {/* Features Grid */}
      <section className="py-24 bg-gradient-to-b from-white to-gray-50 relative">
        {/* Decorative Background Elements */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute top-0 left-1/4 w-64 h-64 bg-blue-100 rounded-full blur-3xl opacity-30" />
          <div className="absolute bottom-0 right-1/4 w-80 h-80 bg-amber-100 rounded-full blur-3xl opacity-30" />
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center mb-20">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}>
              <h2 className="text-5xl font-bold text-[#0f172a] mb-5">
                Explore Nosso Ministério
              </h2>
              <div className="w-24 h-1.5 bg-gradient-to-r from-amber-400 to-orange-500 mx-auto rounded-full mb-6" />
              <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
                Descubra as diferentes formas de conectar-se com Deus e fortalecer sua caminhada espiritual
              </p>
            </motion.div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature, index) =>
            <motion.div
              key={feature.title}
              initial={{ opacity: 0, y: 50, scale: 0.9 }}
              whileInView={{ opacity: 1, y: 0, scale: 1 }}
              transition={{
                duration: 0.7,
                delay: index * 0.15,
                ease: [0.25, 0.46, 0.45, 0.94]
              }}
              viewport={{ once: true, margin: "-50px" }}>
                <Link
                to={feature.link}
                className="group block bg-white rounded-3xl shadow-lg hover:shadow-2xl transition-all duration-300 hover:-translate-y-2 p-8 border border-gray-100 relative overflow-hidden">
                  
                  {/* Gradient Background on Hover */}
                  <div className={`absolute inset-0 bg-gradient-to-br ${feature.gradient} opacity-0 group-hover:opacity-5 transition-opacity duration-300`} />
                  
                  <div className="relative z-10">
                    <div className={`w-20 h-20 rounded-2xl bg-gradient-to-br ${feature.gradient} flex items-center justify-center mb-6 group-hover:scale-110 group-hover:rotate-3 transition-all duration-300 shadow-lg`}>
                      <feature.icon className="text-white" size={36} />
                    </div>
                    
                    <h3 className="text-2xl font-bold mb-4 group-hover:text-transparent group-hover:bg-clip-text group-hover:bg-gradient-to-r group-hover:${feature.gradient} transition-all text-[#172554]">
                      {feature.title}
                    </h3>
                    
                    <p className="leading-relaxed text-base text-[#052E16]">
                      {feature.description}
                    </p>

                    {/* Arrow Icon */}
                    <div className="mt-6 flex items-center text-sm font-semibold group-hover:text-amber-500 transition-colors text-[#F97316]">
                      <span className="group-hover:translate-x-1 transition-transform">Saiba mais</span>
                      <svg className="w-5 h-5 ml-2 group-hover:translate-x-2 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                      </svg>
                    </div>
                  </div>
                </Link>
              </motion.div>
            )}
          </div>
        </div>

        {/* Wave Divider */}
        <div className="absolute bottom-0 left-0 right-0 transform rotate-180">
          <svg viewBox="0 0 1440 80" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full h-auto">
            <path d="M0,48 C360,15 720,75 1080,48 C1440,21 1440,80 1440,80 L0,80 Z" fill="white" />
          </svg>
        </div>
      </section>

      {/* Testimonials Preview Section */}
      <section className="py-24 bg-gradient-to-b from-gray-50 to-white relative overflow-hidden">
        {/* Decorative Background */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute top-0 right-1/4 w-72 h-72 bg-indigo-100 rounded-full blur-3xl opacity-40" />
          <div className="absolute bottom-0 left-1/4 w-80 h-80 bg-amber-100 rounded-full blur-3xl opacity-40" />
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          {/* Section Header */}
          <div className="text-center mb-16">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}>
              <div className="inline-flex items-center gap-2 bg-indigo-50 text-indigo-700 px-4 py-2 rounded-full text-sm font-semibold mb-4 border border-indigo-100">
                <Quote size={16} />
                Vidas Transformadas
              </div>
              <h2 className="text-5xl font-bold text-[#0f172a] mb-5">
                Testemunhos da Nossa Comunidade
              </h2>
              <div className="w-24 h-1.5 bg-gradient-to-r from-indigo-500 to-blue-600 mx-auto rounded-full mb-6" />
              <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
                Histórias reais de fé, esperança e transformação vividas pelos membros do REFUGE IN CHRIST MINISTRY
              </p>
            </motion.div>
          </div>

          {/* Testimonials Cards */}
          {loadingTestimonials ? (
            <div className="text-center py-16">
              <div className="inline-block animate-spin rounded-full h-16 w-16 border-b-4 border-indigo-500"></div>
              <p className="mt-6 text-gray-600 text-lg">Carregando testemunhos...</p>
            </div>
          ) : recentTestimonials.length > 0 ? (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-14">
                {recentTestimonials.map((testimonial, index) => (
                  <motion.div
                    key={testimonial._id}
                    initial={{ opacity: 0, y: 40, scale: 0.95 }}
                    whileInView={{ opacity: 1, y: 0, scale: 1 }}
                    transition={{ duration: 0.6, delay: index * 0.1, ease: [0.25, 0.46, 0.45, 0.94] }}
                    viewport={{ once: true, margin: '-40px' }}
                    className="group bg-white rounded-3xl shadow-md hover:shadow-2xl transition-all duration-300 hover:-translate-y-2 border border-gray-100 overflow-hidden flex flex-col"
                  >
                    {/* Card Top Accent */}
                    <div className="h-1.5 bg-gradient-to-r from-indigo-500 via-blue-500 to-purple-500" />

                    <div className="p-7 flex flex-col flex-1">
                      {/* Quote Icon */}
                      <div className="w-10 h-10 bg-indigo-50 rounded-full flex items-center justify-center mb-5 group-hover:bg-indigo-100 transition-colors">
                        <Quote className="text-indigo-500" size={18} />
                      </div>

                      {/* Message Preview */}
                      <p className="text-gray-700 leading-relaxed text-base mb-6 line-clamp-4 flex-1">
                        {testimonial.message?.length > 180
                          ? testimonial.message.slice(0, 180).trimEnd() + '...'
                          : testimonial.message}
                      </p>

                      {/* Divider */}
                      <div className="border-t border-gray-100 pt-4 mt-auto">
                        {/* Author & Date */}
                        <div className="flex items-center justify-between mb-4">
                          <div className="flex items-center gap-3">
                            <div className="w-9 h-9 bg-gradient-to-br from-indigo-400 to-blue-500 rounded-full flex items-center justify-center text-white font-bold text-sm shadow">
                              {testimonial.name?.charAt(0)?.toUpperCase() || 'A'}
                            </div>
                            <div>
                              <p className="font-semibold text-[#0f172a] text-sm leading-tight">{testimonial.name || 'Anônimo'}</p>
                              <p className="text-xs text-gray-400">
                                {testimonial.createdAt
                                  ? new Date(testimonial.createdAt).toLocaleDateString('pt-BR', {
                                      day: '2-digit',
                                      month: 'short',
                                      year: 'numeric'
                                    })
                                  : ''}
                              </p>
                            </div>
                          </div>
                        </div>

                        {/* Read More Button */}
                        <Link
                          to="/testemunhos"
                          className="w-full flex items-center justify-center gap-2 bg-gradient-to-r from-indigo-50 to-blue-50 hover:from-indigo-500 hover:to-blue-600 text-indigo-600 hover:text-white border border-indigo-100 hover:border-transparent py-3 px-5 rounded-2xl text-sm font-semibold transition-all duration-300 group-hover:shadow-md"
                        >
                          <span>Ler testemunho completo</span>
                          <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
                        </Link>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>

              {/* CTA Ver Todos */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
                viewport={{ once: true }}
                className="text-center">
                <Link
                  to="/testemunhos"
                  className="inline-flex items-center gap-3 bg-gradient-to-r from-[#1e3a8a] to-[#3b82f6] text-white px-10 py-5 rounded-full font-bold text-lg hover:shadow-2xl hover:shadow-blue-500/40 transition-all hover:scale-105">
                  <Users size={22} />
                  Ver Todos os Testemunhos
                  <ArrowRight size={20} />
                </Link>
              </motion.div>
            </>
          ) : (
            <div className="text-center py-16 bg-white rounded-3xl shadow-sm border border-gray-100">
              <Quote className="mx-auto text-gray-300 mb-6" size={64} />
              <p className="text-gray-500 mb-6 text-lg">Seja o primeiro a compartilhar seu testemunho</p>
              <Link
                to="/testemunhos"
                className="inline-flex items-center gap-2 bg-gradient-to-r from-indigo-500 to-blue-600 text-white px-8 py-4 rounded-full font-bold hover:shadow-xl transition-all hover:scale-105">
                <Heart size={18} />
                Compartilhar Testemunho
              </Link>
            </div>
          )}
        </div>

        {/* Wave Divider */}
        <div className="absolute bottom-0 left-0 right-0">
          <svg viewBox="0 0 1440 80" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full h-auto">
            <path d="M0,40 C480,80 960,0 1440,40 L1440,80 L0,80 Z" fill="white" />
          </svg>
        </div>
      </section>

      {/* Prayer Wall Section */}
      <section className="py-24 bg-white relative">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-20">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}>
              <h2 className="text-5xl font-bold text-[#0f172a] mb-5">
                Pedidos de Oração da Comunidade
              </h2>
              <div className="w-24 h-1.5 bg-gradient-to-r from-blue-500 to-indigo-600 mx-auto rounded-full mb-6" />
              <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
                Una-se a nós em oração pelos pedidos de nossa comunidade de fé
              </p>
            </motion.div>
          </div>

          {loadingPrayers ?
          <div className="text-center py-16">
              <div className="inline-block animate-spin rounded-full h-16 w-16 border-b-4 border-amber-500"></div>
              <p className="mt-6 text-gray-600 text-lg">Carregando pedidos...</p>
            </div> :
          recentPrayers.length > 0 ?
          <>
              <PrayerWall prayers={recentPrayers} loading={false} limit={5} />
              <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
              className="text-center mt-12">
                <Link
                to="/oracao"
                className="inline-flex items-center gap-3 bg-gradient-to-r from-[#0f172a] to-[#1e293b] text-white px-10 py-5 rounded-full font-bold text-lg hover:shadow-2xl hover:shadow-gray-500/50 transition-all hover:scale-105">
                  <Heart size={22} />
                  Ver Todos os Pedidos e Enviar o Seu
                </Link>
              </motion.div>
            </> :

          <div className="text-center py-16 bg-gray-50 rounded-3xl">
              <Heart className="mx-auto text-gray-300 mb-6" size={64} />
              <p className="text-gray-500 mb-6 text-lg">Ainda não há pedidos públicos aprovados</p>
              <Link
              to="/oracao"
              className="inline-block bg-gradient-to-r from-amber-400 to-orange-500 text-white px-8 py-4 rounded-full font-bold hover:shadow-xl transition-all hover:scale-105">
                Seja o primeiro a compartilhar
              </Link>
            </div>
          }
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-gradient-to-br from-[#0f172a] via-[#1e293b] to-[#334155] relative overflow-hidden">
        {/* Decorative Elements */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute top-10 left-10 w-64 h-64 bg-amber-400/10 rounded-full blur-3xl" />
          <div className="absolute bottom-10 right-10 w-80 h-80 bg-blue-400/10 rounded-full blur-3xl" />
        </div>

        <div className="max-w-5xl mx-auto px-4 text-center relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}>
            <h2 className="text-5xl md:text-6xl font-bold text-white mb-6 leading-tight">
              Venha Fazer Parte da Nossa Família
            </h2>
            <p className="text-xl md:text-2xl text-white/90 mb-10 leading-relaxed max-w-3xl mx-auto">
              Junte-se a nós nos cultos e encontros. Você é sempre bem-vindo em nossa comunidade de fé!
            </p>
            <div className="flex flex-col sm:flex-row gap-5 justify-center">
              <Link
                to="/eventos"
                className="group bg-gradient-to-r from-amber-400 to-yellow-500 text-[#0f172a] px-10 py-5 rounded-full font-bold text-lg hover:shadow-2xl hover:shadow-amber-500/50 transition-all hover:scale-105">
                <span className="flex items-center justify-center gap-2">
                  <Calendar size={22} />
                  Ver Próximos Eventos
                </span>
              </Link>
              <Link
                to="/contato"
                className="bg-white/10 backdrop-blur-md border-2 border-white/50 text-white px-10 py-5 rounded-full font-bold text-lg hover:bg-white hover:text-[#0f172a] transition-all hover:scale-105 hover:shadow-2xl">
                Fale Conosco
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </div>);

};

export default Home;